/**
 * IRI API
 *
 * Endpoints for UPC/brand validation against IRI cache.
 */

import { apiClient } from './client';
import type { IriValidateResponse } from './generated/contract-types';

export type { IriValidateResponse };

export const validateIri = async (upc: string, brand: string): Promise<IriValidateResponse> => {
  const trimmedUpc = upc.trim();
  const trimmedBrand = brand.trim();
  const response = await apiClient.get<IriValidateResponse>('/iri/validate', {
    params: { upc: trimmedUpc, brand: trimmedBrand },
  });

  return response.data;
};

export interface ExternalSourcePreviewRequest {
  upcs: string[];
  includeProductData: boolean;
  includeImages: boolean;
  limit?: number;
}

export interface ExternalSourceStatus {
  source: string;
  ok: boolean;
  rowCount: number;
  error: string | null;
}

export interface ExternalPreviewConflict {
  field: string;
  options: Array<{
    value: string;
    sourceTable: string;
    confidenceScore: number;
  }>;
}

export interface ExternalProductCandidate {
  upc: string;
  matchedIdentifiers: string[];
  sourceTables: string[];
  confidenceScore: number;
  matchCount: number;
  fields: Record<string, string>;
  conflicts: ExternalPreviewConflict[];
  warnings: string[];
}

export interface ExternalImageCandidate {
  upc: string;
  imageUrl: string;
  imageType: 'front' | 'side' | 'top';
  brand?: string;
  package?: string;
  sourceTable: string;
  matchedIdentifier: string;
  confidenceScore: number;
  sourceUpdatedAt?: string;
  cropBox?: { x: number; y: number; width: number; height: number };
  sourcePosition?: {
    tagPositionVertical?: number;
    tagPositionHorizontal?: number;
    stackIndex?: number;
    tagImageOrdinal?: number;
    pfpFacings?: number;
    tagPosition?: string;
    shelfCount?: number;
    rowPositionCount?: number;
  };
}

export interface ExternalSourcePreviewResponse {
  generatedAt: string;
  mode: 'staging_preview';
  inputs: string[];
  sourceStatus: ExternalSourceStatus[];
  productCandidates: ExternalProductCandidate[];
  imageCandidates: ExternalImageCandidate[];
}

export interface ExternalIntakeResponse {
  receivedCount: number;
  createdCount: number;
  createdSubmissionIds: number[];
  refreshedCount?: number;
  refreshedSubmissionIds?: number[];
  skippedExistingProductCount?: number;
  skippedDuplicateSubmissionCount: number;
  skippedDeclinedCandidateCount?: number;
  invalidCount: number;
  errors: string[];
}

export const previewExternalSources = async (
  request: ExternalSourcePreviewRequest
): Promise<ExternalSourcePreviewResponse> => {
  const response = await apiClient.post<ExternalSourcePreviewResponse>(
    '/iri/external-source-preview',
    request
  );
  return response.data;
};

export const applyProductDataCandidates = async (
  candidates: ExternalProductCandidate[]
): Promise<ExternalIntakeResponse> => {
  const response = await apiClient.post<ExternalIntakeResponse>(
    '/products/submissions/product-data-intake',
    {
      includeExistingProducts: true,
      refreshAutomatedOpenSubmissions: true,
      candidates: candidates.map((candidate) => ({
        upc: candidate.upc,
        ...candidate.fields,
        sourceTables: candidate.sourceTables,
        matchedIdentifiers: candidate.matchedIdentifiers,
        confidenceScore: candidate.confidenceScore,
        submissionNotes: [
          'Product Data POC candidate for admin review.',
          `Provisional sources: ${candidate.sourceTables.join(', ')}.`,
          candidate.conflicts.length > 0
            ? `${candidate.conflicts.length} field conflict(s) require review.`
            : null,
        ]
          .filter(Boolean)
          .join('\n'),
      })),
    }
  );
  return response.data;
};

export const applyIrisImageCandidates = async (
  candidates: ExternalImageCandidate[]
): Promise<ExternalIntakeResponse> => {
  const response = await apiClient.post<ExternalIntakeResponse>(
    '/products/submissions/iris-image-intake',
    { candidates }
  );
  return response.data;
};
